// Copied from platform-app components/pos/receipt/escpos.ts on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
/**
 * Minimal ESC/POS encoder for RONGTA-class thermal printers.
 *
 * Encodes a printer-receipt config + sample order into a Uint8Array
 * stream of ESC/POS bytes that any standard 80mm or 58mm thermal
 * printer (ESC/POS-compatible) can render directly.
 *
 * Phase 1 scope:
 *  - Text blocks (size, alignment, bold, underline, double-strike)
 *  - Boxed announcements (rendered as bordered text frames)
 *  - Dividers (dashed/solid/double via ASCII chars)
 *  - Spacers (line feeds)
 *  - QR codes via the printer's native GS ( k command (real, scannable)
 *  - Auto paper cut at end (GS V)
 *  - Locked sections: business details (with override), order header, items, totals
 *
 * Logo / image bitmap rendering is intentionally NOT in this phase —
 * raster image encoding is another ~80 lines and can land as Phase 2.
 * Logo blocks are silently skipped here; they still print fine via the
 * browser-print fallback.
 */

import type { PrinterReceiptConfig, ReceiptBlock, BusinessDetailLine } from '@platform/shared-types';
import { DEFAULT_BUSINESS_DETAIL_STYLES } from './blockDefaults';
import type { ReceiptOrder } from './ReceiptPreview';

// --- ESC/POS command bytes ---
const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

const CMD = {
  init: [ESC, 0x40],                                // ESC @
  alignLeft: [ESC, 0x61, 0x00],                     // ESC a 0
  alignCenter: [ESC, 0x61, 0x01],                   // ESC a 1
  alignRight: [ESC, 0x61, 0x02],                    // ESC a 2
  boldOn: [ESC, 0x45, 0x01],                        // ESC E 1
  boldOff: [ESC, 0x45, 0x00],
  underlineOn: [ESC, 0x2d, 0x01],                   // ESC - 1
  underlineOff: [ESC, 0x2d, 0x00],
  doubleStrikeOn: [ESC, 0x47, 0x01],                // ESC G 1
  doubleStrikeOff: [ESC, 0x47, 0x00],
  inverseOn: [GS, 0x42, 0x01],                      // GS B 1
  inverseOff: [GS, 0x42, 0x00],
  // Character size: GS ! n  — n bits 0-3 = width-1, bits 4-7 = height-1
  size: (w: 1 | 2 | 3 | 4, h: 1 | 2 | 3 | 4) => [GS, 0x21, ((w - 1) << 4) | (h - 1)],
  cutPartial: [GS, 0x56, 0x01],                     // GS V 1
  cutFull: [GS, 0x56, 0x00],                        // GS V 0
  feed: (n: number) => [ESC, 0x64, Math.max(0, Math.min(255, n))],  // ESC d n  — feed n lines
};

// --- helpers ---

const pushBytes = (out: number[], bytes: number[]) => { for (const b of bytes) out.push(b); };

// CP437 encoding for printable ASCII; falls back to '?' for non-ASCII
const encodeText = (s: string): number[] => {
  const out: number[] = [];
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    out.push(c < 128 ? c : 0x3f);  // '?'
  }
  return out;
};

const fmt = (n: number) => n.toFixed(2);

const widthChars = (w: '80mm' | '58mm') => (w === '80mm' ? 48 : 32);

// ---------------------------------------------------------------------------
// Size mapping — translate our config sizes (S/N/L/XL or S/M/L) to GS ! values.
// ---------------------------------------------------------------------------
const sizeFor = (s: 'S' | 'N' | 'L' | 'XL'): [1 | 2 | 3 | 4, 1 | 2 | 3 | 4] => {
  switch (s) {
    case 'S':  return [1, 1];
    case 'N':  return [1, 1];
    case 'L':  return [2, 2];
    case 'XL': return [3, 3];
  }
};

const alignFor = (a: 'L' | 'C' | 'R') => ({ L: CMD.alignLeft, C: CMD.alignCenter, R: CMD.alignRight }[a]);

const dividerChar = (s: 'dashed' | 'solid' | 'double') => ({ dashed: '-', solid: '=', double: '=' }[s]);

// ---------------------------------------------------------------------------
// QR code: GS ( k command set
// ---------------------------------------------------------------------------
const qrCommands = (data: string, size: 'S' | 'M' | 'L'): number[] => {
  const out: number[] = [];
  // Model 2
  pushBytes(out, [GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00]);
  // Module size: 1..16, larger = bigger printed QR
  const moduleSize = ({ S: 5, M: 8, L: 12 }[size]);
  pushBytes(out, [GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, moduleSize]);
  // Error correction level: M (49)
  pushBytes(out, [GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, 0x31]);
  // Store data
  const bytes = encodeText(data);
  const len = bytes.length + 3;
  pushBytes(out, [GS, 0x28, 0x6b, len & 0xff, (len >> 8) & 0xff, 0x31, 0x50, 0x30]);
  pushBytes(out, bytes);
  // Print
  pushBytes(out, [GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30]);
  return out;
};

// ---------------------------------------------------------------------------
// Block renderer (async because logo/image blocks load + rasterize an image)
// ---------------------------------------------------------------------------
const renderBlock = async (b: ReceiptBlock, w: number, paperDots: number, logoUrl: string | null): Promise<number[]> => {
  const out: number[] = [];

  // spacing-above (none → 0, S → 1, L → 2)
  if (b.type !== 'spacer' && 'spacingAbove' in b && b.spacingAbove !== 'none') {
    pushBytes(out, CMD.feed(b.spacingAbove === 'S' ? 1 : 2));
  }

  switch (b.type) {
    case 'text': {
      pushBytes(out, alignFor(b.align));
      const [sw, sh] = sizeFor(b.size);
      pushBytes(out, CMD.size(sw, sh));
      if (b.bold) pushBytes(out, CMD.boldOn);
      if (b.underline) pushBytes(out, CMD.underlineOn);
      if (b.inverse) pushBytes(out, CMD.inverseOn);
      // italic isn't supported on most thermal printers — render as double-strike for emphasis
      if (b.italic) pushBytes(out, CMD.doubleStrikeOn);
      pushBytes(out, encodeText(b.content));
      pushBytes(out, [LF]);
      // reset
      if (b.italic) pushBytes(out, CMD.doubleStrikeOff);
      if (b.inverse) pushBytes(out, CMD.inverseOff);
      if (b.underline) pushBytes(out, CMD.underlineOff);
      if (b.bold) pushBytes(out, CMD.boldOff);
      pushBytes(out, CMD.size(1, 1));
      break;
    }

    case 'boxed': {
      pushBytes(out, alignFor(b.align));
      const [sw, sh] = sizeFor(b.size);
      // top border
      pushBytes(out, encodeText(dividerChar(b.borderStyle).repeat(w)));
      pushBytes(out, [LF]);
      // content
      pushBytes(out, CMD.size(sw, sh));
      if (b.bold) pushBytes(out, CMD.boldOn);
      pushBytes(out, encodeText(b.content));
      pushBytes(out, [LF]);
      if (b.bold) pushBytes(out, CMD.boldOff);
      pushBytes(out, CMD.size(1, 1));
      // bottom border
      pushBytes(out, encodeText(dividerChar(b.borderStyle).repeat(w)));
      pushBytes(out, [LF]);
      break;
    }

    case 'logo':
    case 'image': {
      const url = b.type === 'logo' ? logoUrl : b.imageUrl;
      if (!url) break;
      const maxDots = logoMaxDots(b.maxWidth, paperDots);
      const raster = await rasterizeImage(url, maxDots);
      if (!raster) break;
      pushBytes(out, alignFor(b.align));
      pushBytes(out, rasterCommands(raster));
      // GS v 0 doesn't always emit a trailing LF — make sure we're on a new line
      pushBytes(out, [LF]);
      break;
    }

    case 'qr': {
      if (!b.data) break;
      pushBytes(out, alignFor(b.align));
      pushBytes(out, qrCommands(b.data, b.size));
      pushBytes(out, [LF]);
      if (b.caption) {
        pushBytes(out, alignFor(b.align));
        pushBytes(out, encodeText(b.caption));
        pushBytes(out, [LF]);
      }
      break;
    }

    case 'divider': {
      const ch = dividerChar(b.style);
      pushBytes(out, CMD.alignLeft);
      pushBytes(out, encodeText(ch.repeat(w)));
      pushBytes(out, [LF]);
      break;
    }

    case 'spacer': {
      const n = ({ S: 1, M: 2, L: 4 }[b.height]);
      pushBytes(out, CMD.feed(n));
      break;
    }
  }

  return out;
};

// ---------------------------------------------------------------------------
// Code 128 barcode (GS k command)
// ---------------------------------------------------------------------------
// CODE128 = function 73 (0x49); data preceded by length byte.
// CRITICAL: Code 128 data MUST start with a code-set selector. Without `{B` (or
// `{A`/`{C`), most ESC/POS printers (including RONGTA) silently print nothing.
// `{B` selects code-set B (printable ASCII ≥ 0x20) — fits typical order numbers.
const barcodeCommands = (data: string): number[] => {
  const out: number[] = [];
  // HRI position: below barcode  (GS H 2)
  pushBytes(out, [GS, 0x48, 0x02]);
  // HRI font: A (GS f 0)
  pushBytes(out, [GS, 0x66, 0x00]);
  // Bar height: 60 dots
  pushBytes(out, [GS, 0x68, 60]);
  // Module width: 2 (smallest readable on 80mm thermal)
  pushBytes(out, [GS, 0x77, 0x02]);
  // Build payload: 0x7B 0x42  +  data bytes  (= "{B" + data)
  const dataBytes = encodeText(data);
  const payload: number[] = [0x7b, 0x42, ...dataBytes];
  const n = Math.min(payload.length, 255);
  // GS k m n d1...dn   (m=73 → CODE128, n = byte count)
  pushBytes(out, [GS, 0x6b, 0x49, n]);
  for (let i = 0; i < n; i++) out.push(payload[i]);
  return out;
};

// ---------------------------------------------------------------------------
// Logo / image rasterization (GS v 0 command)
// ---------------------------------------------------------------------------
async function rasterizeImage(url: string, maxWidthDots: number): Promise<{ widthBytes: number; heightDots: number; data: Uint8Array } | null> {
  if (typeof document === 'undefined') return null;
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = (e) => reject(new Error(`image load failed: ${e}`));
      img.src = url;
    });

    // Snap target width DOWN to a multiple of 8 (printer needs byte-aligned width)
    let widthDots = Math.min(maxWidthDots, img.width);
    widthDots = Math.max(8, Math.floor(widthDots / 8) * 8);
    const heightDots = Math.max(1, Math.round((widthDots / img.width) * img.height));

    const canvas = document.createElement('canvas');
    canvas.width = widthDots;
    canvas.height = heightDots;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, widthDots, heightDots);
    ctx.drawImage(img, 0, 0, widthDots, heightDots);
    const imgData = ctx.getImageData(0, 0, widthDots, heightDots);

    const widthBytes = widthDots / 8;
    const data = new Uint8Array(widthBytes * heightDots);
    // Threshold to monochrome (alpha-aware luminance)
    for (let y = 0; y < heightDots; y++) {
      for (let x = 0; x < widthDots; x++) {
        const idx = (y * widthDots + x) * 4;
        const a = imgData.data[idx + 3];
        const r = imgData.data[idx];
        const g = imgData.data[idx + 1];
        const b = imgData.data[idx + 2];
        const lum = a === 0 ? 255 : 0.299 * r + 0.587 * g + 0.114 * b;
        if (lum < 160) {
          data[y * widthBytes + (x >> 3)] |= 0x80 >> (x & 7);
        }
      }
    }
    return { widthBytes, heightDots, data };
  } catch (err) {
    console.warn('[escpos] rasterizeImage failed', err);
    return null;
  }
}

const rasterCommands = (raster: { widthBytes: number; heightDots: number; data: Uint8Array }): number[] => {
  const xL = raster.widthBytes & 0xff;
  const xH = (raster.widthBytes >> 8) & 0xff;
  const yL = raster.heightDots & 0xff;
  const yH = (raster.heightDots >> 8) & 0xff;
  // GS v 0  m=0  xL xH yL yH  data...
  return [GS, 0x76, 0x30, 0x00, xL, xH, yL, yH, ...raster.data];
};

// Resolve our maxWidth setting to a target dot count, capped by paper width
const logoMaxDots = (mw: 'S' | 'M' | 'L', paperDots: number) => {
  const target = ({ S: 120, M: 240, L: 380 }[mw]);
  return Math.min(target, paperDots);
};

// ---------------------------------------------------------------------------
// Locked sections
// ---------------------------------------------------------------------------

// Apply per-line style commands and emit one styled line of business details.
const renderBusinessLine = (
  out: number[],
  line: BusinessDetailLine | null | undefined,
  defaultContent: string | undefined,
  styleKey: keyof typeof DEFAULT_BUSINESS_DETAIL_STYLES,
): void => {
  const def = DEFAULT_BUSINESS_DETAIL_STYLES[styleKey];
  const o = line || {};
  const content = (o.content && o.content.trim()) || defaultContent;
  if (!content) return;
  const size = o.size ?? def.size;
  const align = o.align ?? def.align;
  const bold = o.bold ?? def.bold;
  const italic = o.italic ?? def.italic;
  const underline = o.underline ?? def.underline;

  pushBytes(out, alignFor(align));
  const [sw, sh] = sizeFor(size);
  pushBytes(out, CMD.size(sw, sh));
  if (bold) pushBytes(out, CMD.boldOn);
  if (underline) pushBytes(out, CMD.underlineOn);
  if (italic) pushBytes(out, CMD.doubleStrikeOn);
  pushBytes(out, encodeText(content));
  pushBytes(out, [LF]);
  if (italic) pushBytes(out, CMD.doubleStrikeOff);
  if (underline) pushBytes(out, CMD.underlineOff);
  if (bold) pushBytes(out, CMD.boldOff);
  pushBytes(out, CMD.size(1, 1));
};

const renderBusinessDetails = (config: PrinterReceiptConfig, order: ReceiptOrder, w: number): number[] => {
  const out: number[] = [];
  const ov = config.businessDetailsOverride || {};
  renderBusinessLine(out, ov.legalName,    order.legalName, 'legalName');
  renderBusinessLine(out, ov.addressLine1, order.address,   'addressLine1');
  renderBusinessLine(out, ov.addressLine2, undefined,       'addressLine2');
  renderBusinessLine(out, ov.addressLine3, undefined,       'addressLine3');
  renderBusinessLine(out, ov.phone,        order.phone,     'phone');
  pushBytes(out, CMD.alignLeft);
  pushBytes(out, encodeText('-'.repeat(w)));
  pushBytes(out, [LF]);
  return out;
};

const renderOrderAndItems = (order: ReceiptOrder, w: number): number[] => {
  const out: number[] = [];
  pushBytes(out, CMD.alignLeft);
  pushBytes(out, encodeText(`Order #${order.orderNumber}`));
  pushBytes(out, [LF]);
  pushBytes(out, encodeText(new Date(order.timestamp).toLocaleString()));
  pushBytes(out, [LF]);
  if (order.cashier) {
    pushBytes(out, encodeText(`Cashier: ${order.cashier}`));
    pushBytes(out, [LF]);
  }
  pushBytes(out, encodeText('- '.repeat(Math.floor(w / 2)).slice(0, w)));
  pushBytes(out, [LF]);

  for (const it of order.items) {
    const left = `${it.qty} x ${it.name}`;
    const priceStr = fmt(it.qty * it.price);
    const dotsLen = Math.max(1, w - left.length - priceStr.length);
    pushBytes(out, encodeText(left + '.'.repeat(dotsLen) + priceStr));
    pushBytes(out, [LF]);
  }
  return out;
};

const renderTotals = (order: ReceiptOrder, w: number): number[] => {
  const out: number[] = [];
  pushBytes(out, CMD.alignLeft);
  pushBytes(out, encodeText('- '.repeat(Math.floor(w / 2)).slice(0, w)));
  pushBytes(out, [LF]);

  pushBytes(out, CMD.alignRight);
  const right = (s: string) => { pushBytes(out, encodeText(s)); pushBytes(out, [LF]); };
  right(`Subtotal: ${fmt(order.subtotal)}`);
  right(`Tax: ${fmt(order.tax)}`);
  for (const f of order.fees || []) right(`${f.label}: ${fmt(f.amount)}`);
  for (const s of order.surcharges || []) right(`${s.label}: ${fmt(s.amount)}`);
  if (order.tip != null) right(`Tip: ${fmt(order.tip)}`);

  pushBytes(out, CMD.boldOn);
  pushBytes(out, CMD.size(2, 2));
  right(`TOTAL: ${fmt(order.total)}`);
  pushBytes(out, CMD.size(1, 1));
  pushBytes(out, CMD.boldOff);

  right(`${order.payment}${order.change != null ? ` Change ${fmt(order.change)}` : ''}`);
  return out;
};

// Paper dot widths (typical thermal printer @ 203dpi):
//   80mm head ≈ 576 dots, 58mm head ≈ 384 dots
const paperDotsFor = (width: '80mm' | '58mm') => (width === '80mm' ? 576 : 384);

// ---------------------------------------------------------------------------
// Top-level encoder (async because logo/image blocks await image loading)
// ---------------------------------------------------------------------------
export async function encodeReceipt(config: PrinterReceiptConfig, order: ReceiptOrder, width: '80mm' | '58mm'): Promise<Uint8Array> {
  const w = widthChars(width);
  const paperDots = paperDotsFor(width);
  const out: number[] = [];

  pushBytes(out, CMD.init);

  for (const b of config.slots.header) pushBytes(out, await renderBlock(b, w, paperDots, config.logoUrl));
  pushBytes(out, renderBusinessDetails(config, order, w));
  pushBytes(out, renderOrderAndItems(order, w));
  for (const b of config.slots.belowItems) pushBytes(out, await renderBlock(b, w, paperDots, config.logoUrl));
  pushBytes(out, renderTotals(order, w));

  // Locked: order barcode (default, always shown)
  if (order.orderNumber) {
    pushBytes(out, CMD.feed(1));
    pushBytes(out, CMD.alignCenter);
    pushBytes(out, barcodeCommands(order.orderNumber));
    pushBytes(out, [LF]);
  }

  for (const b of config.slots.footer) pushBytes(out, await renderBlock(b, w, paperDots, config.logoUrl));

  pushBytes(out, CMD.feed(4));
  pushBytes(out, CMD.cutPartial);

  return new Uint8Array(out);
}
